import type { ReactNode } from 'react';

import { InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationTemplate } from '@azure/msal-react';
import { Box } from '@mui/material';
import { Navigate, Outlet, Route, Routes } from 'react-router';

import {
  HomeDashboard,
  ClientRecordsDashboard,
  RelatedClientsDashboard,
  WaiverRecordsDashboard,
} from '@/apps/engagement-letter-database';
import EngagementLetterDatabaseLayout from '@/apps/engagement-letter-database/layouts/AppLayout';
import MainLayout from '@/layouts/MainLayout';
import PageShell from '@/layouts/PageShell';
import { routePaths, routeSegments } from '@/routes/routeConfig';
import CardGrid from '@/shared/components/CardGrid';
import type { CardGridItem } from '@/shared/components/CardGrid';
import NotFound from '@/shared/components/NotFound';
import { navigationConstants } from '@/shared/constants';

const loadingFallback = <Box>Loading...</Box>;
const MsalLoadingComponent = () => loadingFallback;
const notFoundPath = '/not-found';
const authBypassEnabled = import.meta.env.VITE_E2E_AUTH_BYPASS?.trim().toLowerCase() === 'true';

const renderProtectedContent = (content: ReactNode) => {
  if (authBypassEnabled) return content;

  return (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} loadingComponent={MsalLoadingComponent}>
      {content}
    </MsalAuthenticationTemplate>
  );
};

const ProtectedOutlet = () => renderProtectedContent(<Outlet />);

const ProtectedMainLayout = () => renderProtectedContent(<MainLayout />);

const AppsPage = () => {
  const appCards: CardGridItem[] = navigationConstants.apps.map((app) => ({
    id: app.id,
    title: app.label,
    description: `${app.label} workspace`,
    icon: app.icon,
    to: app.path,
    actionLabel: `Open ${app.label}`,
  }));

  return (
    <PageShell title={navigationConstants.homeTitle}>
      <CardGrid cards={appCards} />
    </PageShell>
  );
};

const AppRouter = () => (
  <Routes>
    <Route path={`${routePaths.engagementLetterDatabase}/*`} element={<ProtectedMainLayout />}>
      <Route element={<EngagementLetterDatabaseLayout />}>
        <Route index element={<HomeDashboard />} />
        <Route path={routeSegments.engagementLetterDatabaseClient} element={<ClientRecordsDashboard />} />
        <Route path={routeSegments.engagementLetterDatabaseWaiver} element={<WaiverRecordsDashboard />} />
        <Route path={routeSegments.engagementLetterDatabaseRelatedClient} element={<RelatedClientsDashboard />} />
      </Route>
      <Route path="*" element={<Navigate replace to={notFoundPath} />} />
    </Route>
 
    {/* Global portal shell — shared AppBar + Outlet */}
    <Route element={<MainLayout />}>
      <Route element={<ProtectedOutlet />}>
        <Route path={routePaths.root} element={<AppsPage />} />
        <Route path={routeSegments.app} element={<AppsPage />} />
      </Route>
      <Route path="not-found" element={<NotFound />} />
      <Route path="*" element={<Navigate replace to={notFoundPath} />} />
    </Route>
  </Routes>
);

export default AppRouter;
